/**
 * Tally GL create helpers.
 *
 * Prefer API-driven labels / default_parents from GET account-type-groups.
 * Static maps are fallbacks only (offline / older API).
 */

/** Zoho slug → default Tally PARENT (migration / fallback). */
const ZOHO_DEFAULT_PARENT: Record<string, string> = {
  equity: "Capital Account",
  other_current_asset: "Current Assets",
  cash: "Cash-in-Hand",
  stock: "Stock-in-Hand",
  fixed_asset: "Fixed Assets",
  other_asset: "Investments",
  other_current_liability: "Current Liabilities",
  long_term_liability: "Loans (Liability)",
  expense: "Indirect Expenses",
  other_expense: "Indirect Expenses",
  cost_of_goods_sold: "Direct Expenses",
  income: "Indirect Incomes",
  other_income: "Indirect Incomes",
};

/** Tally slug → reserved group display name / default PARENT. */
const TALLY_SLUG_TO_GROUP: Record<string, string> = {
  capital_account: "Capital Account",
  reserves_and_surplus: "Reserves & Surplus",
  current_assets: "Current Assets",
  cash_in_hand: "Cash-in-Hand",
  stock_in_hand: "Stock-in-Hand",
  deposits_asset: "Deposits (Asset)",
  loans_advances_asset: "Loans & Advances (Asset)",
  current_liabilities: "Current Liabilities",
  provisions: "Provisions",
  direct_expenses: "Direct Expenses",
  indirect_expenses: "Indirect Expenses",
  direct_incomes: "Direct Incomes",
  indirect_incomes: "Indirect Incomes",
  fixed_assets: "Fixed Assets",
  investments: "Investments",
  loans_liability: "Loans (Liability)",
  secured_loans: "Secured Loans",
  unsecured_loans: "Unsecured Loans",
  misc_expenses_asset: "Misc. Expenses (ASSET)",
  purchase_accounts: "Purchase Accounts",
  sales_accounts: "Sales Accounts",
  suspense_ac: "Suspense A/c",
  branch_divisions: "Branch / Divisions",
};

function titleCaseSlug(slug: string): string {
  return slug
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

export function defaultTallyParentGroupForAccountType(
  accountType: string,
  defaultParents?: Record<string, string> | null,
  labels?: Record<string, string> | null,
): string {
  const key = accountType.trim().toLowerCase();
  if (!key) {
    return "Current Assets";
  }
  if (defaultParents?.[key]) {
    return defaultParents[key];
  }
  // Reserved roots: label === parent group name.
  if (labels?.[key]) {
    return labels[key];
  }
  return (
    TALLY_SLUG_TO_GROUP[key] ??
    ZOHO_DEFAULT_PARENT[key] ??
    "Current Assets"
  );
}

export function accountTypeDisplayLabel(
  accountType: string,
  labels?: Record<string, string> | null,
): string {
  const key = accountType.trim().toLowerCase();
  if (!key) {
    return "";
  }
  if (labels?.[key]) {
    return labels[key];
  }
  if (TALLY_SLUG_TO_GROUP[key]) {
    return TALLY_SLUG_TO_GROUP[key];
  }
  return titleCaseSlug(key);
}

/** Reserved Tally primary group names that cannot be created/renamed to. */
const RESERVED_PRIMARY_GROUP_NAMES = new Set(
  [
    "Capital Account",
    "Reserves & Surplus",
    "Current Assets",
    "Cash-in-Hand",
    "Stock-in-Hand",
    "Deposits (Asset)",
    "Loans & Advances (Asset)",
    "Current Liabilities",
    "Provisions",
    "Direct Expenses",
    "Indirect Expenses",
    "Direct Incomes",
    "Indirect Incomes",
    "Fixed Assets",
    "Investments",
    "Loans (Liability)",
    "Secured Loans",
    "Unsecured Loans",
    "Misc. Expenses (ASSET)",
    "Purchase Accounts",
    "Sales Accounts",
    "Suspense A/c",
    "Branch / Divisions",
    "Bank Accounts",
    "Bank OD A/c",
    "Credit Cards",
    "Sundry Debtors",
    "Sundry Creditors",
    "Duties & Taxes",
    "Primary",
  ].map((n) => n.toLowerCase()),
);

export function isReservedTallyPrimaryGroupName(name: string): boolean {
  return RESERVED_PRIMARY_GROUP_NAMES.has(name.trim().toLowerCase());
}
